// Helpers for turning a recorded/imported Blob into a seekable HTMLVideoElement.
//
// MediaRecorder output (especially WebM) often reports `duration = Infinity`
// until the element has been seeked to the end — our frame-by-frame analysis
// needs a finite, seekable duration, so we force the browser to compute it.

export interface LoadedVideo {
  video: HTMLVideoElement;
  url: string;
}

export async function loadVideoFromBlob(blob: Blob): Promise<LoadedVideo> {
  const url = URL.createObjectURL(blob);
  const video = document.createElement("video");
  video.src = url;
  video.muted = true;
  video.preload = "auto";
  (video as HTMLVideoElement & { playsInline: boolean }).playsInline = true;

  await new Promise<void>((resolve, reject) => {
    video.onloadedmetadata = () => resolve();
    video.onerror = () => reject(new Error("Could not read this video file."));
  });

  await ensureFiniteDuration(video);
  return { video, url };
}

/** Force computation of a finite duration for MediaRecorder blobs. */
function ensureFiniteDuration(video: HTMLVideoElement): Promise<void> {
  if (isFinite(video.duration) && video.duration > 0) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const onSeeked = () => {
      video.removeEventListener("seeked", onSeeked);
      video.currentTime = 0;
      resolve();
    };
    video.addEventListener("seeked", onSeeked);
    // Seeking far past the end makes the browser resolve the real duration.
    video.currentTime = 1e7;
  });
}

/** Pick the best MediaRecorder mime type the current browser supports. */
export function pickRecorderMimeType(): string | undefined {
  const candidates = [
    "video/mp4;codecs=h264",
    "video/mp4",
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
  ];
  const MR = (globalThis as { MediaRecorder?: typeof MediaRecorder }).MediaRecorder;
  if (!MR || !MR.isTypeSupported) return undefined;
  return candidates.find((t) => MR.isTypeSupported(t));
}
